import { useAlertContext, useConfirmationDialogContext } from "@shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ClientDetailResponse, CreateCallbackOutcome, clientsApi } from "@/api";
import { assertUnreachable } from "@/common/helper/commonHelper";
import { getLocalTimeZoneId } from "@/common/utils/getLocalTimeZoneId";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

export function useClientCallbackActions(client: ClientDetailResponse) {
  const { t } = useTranslation();
  const { showSuccess } = useAlertContext();
  const { showYesNoDialog } = useConfirmationDialogContext();
  const queryClient = useQueryClient();

  const createCallbackAmbulanzMutation = useMutation({
    mutationFn: () =>
      clientsApi.createCallbackAmbulanz({
        clientId: client.clientId,
        createCallbackAmbulanzRequest: { originTimeZoneId: getLocalTimeZoneId() },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.ClientCaseNotes, client.clientId] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Ambulanzes] });
      showSuccess(t("clients.client.masterData.callbackAmbulanzDialog.success"));
    },
    // No onError: failures are surfaced by the global errorMiddleware with the server's German message.
  });

  const getCallbackSuccessMessage = (outcome: CreateCallbackOutcome): string => {
    switch (outcome) {
      case CreateCallbackOutcome.AssignmentForCounselor:
        return t("clients.client.masterData.callbackAssignmentDialog.successAssignmentForCounselor");
      case CreateCallbackOutcome.AssignmentForSubstitute:
        return t("clients.client.masterData.callbackAssignmentDialog.successAssignmentForSubstitute");
      case CreateCallbackOutcome.Ambulanz:
        return t("clients.client.masterData.callbackAssignmentDialog.successAmbulanz");
      default:
        return assertUnreachable(outcome);
    }
  };

  const createCallbackAssignmentMutation = useMutation({
    mutationFn: () =>
      clientsApi.createCallback({
        clientId: client.clientId,
        createCallbackRequest: { originTimeZoneId: getLocalTimeZoneId() },
      }),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.ClientCaseNotes, client.clientId] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Ambulanzes] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.Assignments] });
      showSuccess(getCallbackSuccessMessage(response.outcome));
    },
    // No onError: failures are surfaced by the global errorMiddleware with the server's German message.
  });

  const handleCallbackAmbulanz = () => {
    showYesNoDialog(
      t("clients.client.masterData.callbackAmbulanzDialog.title"),
      t("clients.client.masterData.callbackAmbulanzDialog.text"),
      (confirmed) => {
        if (confirmed) {
          createCallbackAmbulanzMutation.mutate();
        }
      }
    );
  };

  const handleCallbackAssignment = () => {
    showYesNoDialog(
      t("clients.client.masterData.callbackAssignmentDialog.title"),
      t("clients.client.masterData.callbackAssignmentDialog.text"),
      (confirmed) => {
        if (confirmed) {
          createCallbackAssignmentMutation.mutate();
        }
      }
    );
  };

  return {
    handleCallbackAmbulanz,
    handleCallbackAssignment,
    isCallbackAmbulanzPending: createCallbackAmbulanzMutation.isPending,
    isCallbackAssignmentPending: createCallbackAssignmentMutation.isPending,
  };
}
